import { translate } from "app/filters/i18nFilter";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import {
    getAdNetworkSignalEventActionText,
    getSignalEventTitle,
    getTechnologySignalEventText,
} from "app/pages/sales-intelligence/sub-modules/account-overview/company/services/signals/getters";
import {
    getSignalEventDate,
    getSignalEventPeriod,
} from "app/pages/sales-intelligence/sub-modules/account-overview/company/services/signals/getters/getSignalEventDate";
import getSignalEventTags from "app/pages/sales-intelligence/sub-modules/account-overview/company/services/signals/getters/getSignalEventTags";
import getValidSignalEvents from "app/pages/sales-intelligence/sub-modules/account-overview/company/services/signals/getters/getValidSignalEvents";
import type { Topic } from "app/pages/sales-intelligence/sub-modules/intent/types/common";
import type Signal from "app/pages/sales-intelligence/sub-modules/signals/models/core/Signal";
import type SignalFilters from "app/pages/sales-intelligence/sub-modules/signals/models/core/SignalFilters";
import SignalType from "app/pages/sales-intelligence/sub-modules/signals/models/core/SignalType";
import type SignalsGroupedByType from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalsGroupedByType";
import { SignalTimeframePeriod } from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalTimeframe";
import type { SignalTimeframeDTO } from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalTimeframeDTO";
import { isFilterWithTimeframe } from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/type-guards/isFilterWithTimeframe";
import type { NewsCategory } from "app/pages/sales-intelligence/sub-modules/signals/models/news/NewsCategory";
import type { NewsCategoryType } from "app/pages/sales-intelligence/sub-modules/signals/models/news/NewsCategoryType";
import NewsSignalSubType from "app/pages/sales-intelligence/sub-modules/signals/models/news/NewsSignalSubType";
import { isTrafficChangesModel } from "app/pages/sales-intelligence/sub-modules/signals/models/traffic/type-guards/isTrafficChangesModel";
import signalsService from "app/pages/sales-intelligence/sub-modules/signals/services/signalsService";
import { NEWS_CATEGORIES_BY_TYPE } from "app/pages/sales-intelligence/sub-modules/signals/utils/news/const";
import type { ICountryObject } from "app/services/CountryService";
import CountryService from "app/services/CountryService";
import type { SignalEventDto, SignalsEventsGroupedByType, TrafficSignalEventDto } from "../../types/signals";
import { isTrafficSignalEvent } from "../../types/signals";
import { getNewsSignalCategory } from "./getters/getNewsSignalCategory";
import { COMPANY_CHALLENGES } from "./maps/newsSignalEventsTagsMap";
import type {
    SignalEventsFilterConfigItemType,
    SignalEventsService,
    SignalEventsTypeItem,
    SignalEventType,
} from "./types";

const createSignalEventsService = (): SignalEventsService => {
    const validSignalTypes: readonly SignalType[] = [
        SignalType.News,
        SignalType.Traffic,
        SignalType.Ecommerce,
        SignalType.Technology,
        SignalType.AdNetwork,
    ];

    const getSignalTypeEvents = (): readonly SignalType[] =>
        salesSettingsHelper.isIntentSignalsEnabled() ? [SignalType.Intent, ...validSignalTypes] : validSignalTypes;

    const signalSubTypeEvents: readonly NewsSignalSubType[] = Object.values(NewsSignalSubType);

    const ORDERED_EVENT_TYPES: readonly SignalEventType[] = [
        "All",
        SignalType.Intent,
        SignalType.Technology,
        SignalType.AdNetwork,
        SignalType.Traffic,
        SignalType.Ecommerce,
        NewsSignalSubType.Investment,
        NewsSignalSubType.Leadership,
        NewsSignalSubType.Growth,
        NewsSignalSubType.CompanyChallenges,
        NewsSignalSubType.Recognition,
        NewsSignalSubType.Strategy,
    ];

    const isSignalEventOfType = (eventType: SignalEventType): boolean =>
        getSignalTypeEvents().includes(eventType as SignalType);
    const isSignalEventOfSubType = (eventType: SignalEventType): boolean =>
        signalSubTypeEvents.includes(eventType as NewsSignalSubType);

    const isValidSignalEventTypeItem = ({ value, count, hasSignal }: Readonly<SignalEventsTypeItem>): boolean =>
        count > 0 && hasSignal && value !== "All";

    const getSignalEventsByType = (
        signalEvents: readonly SignalEventDto[],
        type: SignalType,
    ): readonly SignalEventDto[] => signalEvents.filter((signalEvent) => signalEvent.type === type);

    const groupSignalEventsBySignalType = (signalEvents: SignalEventDto[]): Readonly<SignalsEventsGroupedByType> => {
        const initialGroup = signalsService.getEmptyCollectionsGroupedBySignalType() as SignalsEventsGroupedByType;

        return signalEvents.reduce<SignalsEventsGroupedByType>((group, signalEvent) => {
            const signalEventType = signalEvent.type;

            if (Array.isArray(group[signalEventType])) {
                group[signalEventType].push(signalEvent);
            }

            return group;
        }, initialGroup);
    };

    const getSignalEventsByNewsSubType = (
        signalEvents: readonly SignalEventDto[],
        subType: NewsSignalSubType,
    ): readonly SignalEventDto[] =>
        signalEvents.filter((signalEvent) => {
            if (signalEvent.type === SignalType.News) {
                return getNewsSignalCategory(signalEvent) === subType;
            }
            return false;
        });

    const getSignalEventsOfType = (
        signalEvents: readonly SignalEventDto[],
        eventType: SignalEventType,
    ): readonly SignalEventDto[] => {
        if (isSignalEventOfType(eventType)) {
            return getSignalEventsByType(signalEvents, eventType as SignalType);
        }
        if (isSignalEventOfSubType(eventType)) {
            return getSignalEventsByNewsSubType(signalEvents, eventType as NewsSignalSubType);
        }
        return signalEvents;
    };

    const getSignalEventsOfTypeCount = (
        signalEvents: readonly SignalEventDto[],
        filterValue: SignalEventType,
    ): number => getSignalEventsOfType(signalEvents, filterValue).length;

    const getFilterTooltip = (value: SignalEventType): string =>
        translate(`si.account-overview.signals.filter.${value.toLowerCase()}.tooltip`);

    const isSignalPresentForEventType = (
        signalsGroupedByType: Readonly<SignalsGroupedByType>,
        value: SignalEventType,
    ): boolean => {
        if (value === "All") {
            return Object.values(signalsGroupedByType).flat().length > 0;
        }

        if (isSignalEventOfType(value)) {
            return signalsGroupedByType[value as SignalType]?.length > 0;
        }

        if (isSignalEventOfSubType(value)) {
            return signalsGroupedByType[SignalType.News].some((signal) => {
                const categoriesBySubType = NEWS_CATEGORIES_BY_TYPE[value as NewsCategoryType] || [];
                const activeSignalCategories =
                    (signal.filters as SignalFilters & { categories: readonly NewsCategory[] }).categories || [];

                return activeSignalCategories.some((key) => categoriesBySubType.includes(key));
            });
        }

        return false;
    };

    const getEventTypeLabel = (value: SignalEventType): string => {
        switch (value) {
            case NewsSignalSubType.CompanyChallenges:
                return COMPANY_CHALLENGES;
            case SignalType.AdNetwork:
                return "Ad networks";
            case SignalType.Ecommerce:
                return translate(`si.signals.type.${SignalType.Ecommerce}.text`);
            default:
                return value.toString();
        }
    };

    const getSignalEventsTypeItem = (
        signalEvents: readonly SignalEventDto[],
        value: SignalEventType,
        signalsGroupedByType: SignalsGroupedByType,
    ): Readonly<SignalEventsTypeItem> => {
        return {
            value,
            label: getEventTypeLabel(value),
            hasSignal: isSignalPresentForEventType(signalsGroupedByType, value),
            count: getSignalEventsOfTypeCount(signalEvents, value),
        };
    };

    const getSignalEventsFilterConfigBaseItem = (
        signalEvents: readonly SignalEventDto[],
        value: SignalEventType,
        signalsGroupedByType: SignalsGroupedByType,
    ): Readonly<SignalEventsFilterConfigItemType> => {
        const { hasSignal, ...eventsTypeItemBase } = getSignalEventsTypeItem(signalEvents, value, signalsGroupedByType);

        return {
            ...eventsTypeItemBase,
            active: hasSignal,
            tooltip: value === "All" ? undefined : getFilterTooltip(value),
        };
    };

    const getSignalEventType = (
        signalEvent: Readonly<SignalEventDto>,
        signalsGroupedByType: SignalsGroupedByType,
    ): SignalEventType => {
        const matchedType = ORDERED_EVENT_TYPES.find((eventType) => {
            return isValidSignalEventTypeItem(getSignalEventsTypeItem([signalEvent], eventType, signalsGroupedByType));
        });

        if (typeof matchedType === "undefined") {
            throw new Error(`Unable to match event type: ${signalEvent.type}`);
        }

        return matchedType;
    };

    const getOrderedSignalEventsTypeItems = (
        signalEvents: readonly SignalEventDto[],
        signalsGroupedByType: SignalsGroupedByType,
    ): readonly SignalEventsTypeItem[] => {
        return ORDERED_EVENT_TYPES.map((eventType) =>
            getSignalEventsTypeItem(signalEvents, eventType, signalsGroupedByType),
        );
    };

    const getSignalEventsByIntentTopics = (
        filterConfig: readonly SignalEventsFilterConfigItemType[],
        selectedIntentTopics: readonly Topic[],
    ): readonly SignalEventsFilterConfigItemType[] => {
        if (selectedIntentTopics.length === 0) {
            return filterConfig.filter((filter) => filter.value !== SignalType.Intent);
        }

        return filterConfig;
    };

    const getSignalEventsFilterConfig = (
        signalEvents: readonly SignalEventDto[],
        signalsGroupedByType: SignalsGroupedByType,
    ): readonly SignalEventsFilterConfigItemType[] => {
        const orderedConfig = ORDERED_EVENT_TYPES.map((key) =>
            getSignalEventsFilterConfigBaseItem(signalEvents, key, signalsGroupedByType),
        );
        const orderedActiveConfig = orderedConfig.filter((configItem) => configItem.active);
        const orderedInactiveConfig = orderedConfig.filter((configItem) => !configItem.active);
        return [...orderedActiveConfig, ...orderedInactiveConfig];
    };

    const getSignalEventsTimeFrame = (signal: Signal | undefined): SignalTimeframeDTO => {
        let periodAmount = 1;
        let period: SignalTimeframePeriod = SignalTimeframePeriod.Month;

        const shouldGetTimeFrameFromSignal = signal && isFilterWithTimeframe(signal.filters);

        if (shouldGetTimeFrameFromSignal) {
            ({ period, periodAmount } = signal.filters);
        }

        return { period, periodAmount };
    };

    const getTrafficSignalCountryId = (signal: Readonly<TrafficSignalEventDto>): number | undefined => {
        const { filters } = signal;

        if (isTrafficChangesModel(filters.trafficChanges)) {
            return filters.trafficChanges.country;
        }

        return undefined;
    };

    const getSignalEventCountry = (signalEvent: Readonly<SignalEventDto>): ICountryObject | undefined => {
        let countryId: number | undefined;

        if (isTrafficSignalEvent(signalEvent)) {
            countryId = getTrafficSignalCountryId(signalEvent);
        }

        return countryId ? CountryService.getCountryById(countryId) : undefined;
    };

    return {
        isTypeFilter: isSignalEventOfType,
        isSubTypeFilter: isSignalEventOfSubType,
        isValidSignalEventTypeItem,
        getSignalEventsOfType,
        getSignalEventsOfTypeCount,
        getSignalEventsFilterConfig,
        getOrderedSignalEventsTypeItems,
        getSignalEventType,
        groupSignalEventsBySignalType,

        getNewsSignalCategory,
        getSignalEventPeriod,
        getSignalEventDate,
        getSignalEventTags,
        getTechnologySignalEventText,
        getAdNetworkSignalEventActionText,
        getSignalEventTitle,
        getValidSignalEvents,

        getSignalEventsTimeFrame,
        getSignalEventCountry,
        getSignalEventsByIntentTopics,
    };
};

export default createSignalEventsService;
